/**
 * The Phase 5B operations: failure analyses of workflow runs, deployments and
 * external checks — requesting one, the overview of one source, reading an
 * analysis and its evidence, feedback and recovery verification — plus the
 * run, job and deployment detail reads the pages that host the panel need.
 *
 * Split from `operations.ts` for length and re-exported from it, as the other
 * phases are. One function per `/v1` operation, paths checked against the
 * generated contract.
 */

import { expect, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type FailureAnalysis = Schemas['FailureAnalysis'];
export type FailureAnalysisOverview = Schemas['FailureAnalysisOverview'];
export type FailureAnalysisState = Schemas['FailureAnalysisState'];
export type FailureSourceKind = Schemas['FailureSourceKind'];
export type FailureCategory = Schemas['FailureCategory'];
export type FailureConfidence = Schemas['FailureConfidence'];
export type FailureEvidence = Schemas['FailureEvidence'];
export type FailureCause = Schemas['FailureCause'];
export type FailureRecommendation = Schemas['FailureRecommendation'];
export type FailureRelatedChange = Schemas['FailureRelatedChange'];
export type FailureRecovery = Schemas['FailureRecovery'];
export type FailureRecoveryCondition = Schemas['FailureRecoveryCondition'];
export type FailureAnalysisEvidence = Schemas['FailureAnalysisEvidence'];
export type FailureAnalysisFeedback = Schemas['FailureAnalysisFeedback'];
export type FailureFeedbackKind = Schemas['FailureAnalysisFeedbackCreate']['kind'];
export type WorkflowJobDetail = Schemas['WorkflowJobDetail'];
export type WorkflowJob = Schemas['WorkflowJob'];
export type WorkflowJobQueue = Schemas['WorkflowJobQueue'];
export type JobLogText = Schemas['JobLogText'];
export type JobLogTextChunk = Schemas['JobLogTextChunk'];
export type WorkflowStep = Schemas['WorkflowStep'];

interface RepoScope {
  orgId: string;
  repoId: string;
}

const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });

export function getFailureAnalysisOverview(
  scope: RepoScope,
  sourceKind: FailureSourceKind,
  sourceId: string,
): Promise<FailureAnalysisOverview> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/failure-analyses/overview', {
      params: { path: repoPath(scope), query: { source_kind: sourceKind, source_id: sourceId } },
    }),
  );
}

export function requestFailureAnalysis(
  scope: RepoScope,
  body: { source_kind: FailureSourceKind; source_id: string; reanalyze?: boolean },
): Promise<FailureAnalysis> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/failure-analyses', {
      params: { path: repoPath(scope) },
      body: { ...body, reanalyze: body.reanalyze ?? false },
    }),
  );
}

export function getFailureAnalysis(scope: RepoScope, analysisId: string): Promise<FailureAnalysis> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/failure-analyses/{analysis_id}', {
      params: { path: { ...repoPath(scope), analysis_id: analysisId } },
    }),
  );
}

export function getFailureAnalysisEvidence(
  scope: RepoScope,
  analysisId: string,
): Promise<FailureAnalysisEvidence> {
  return expect(
    http.GET(
      '/organizations/{org_id}/repositories/{repo_id}/failure-analyses/{analysis_id}/evidence',
      { params: { path: { ...repoPath(scope), analysis_id: analysisId } } },
    ),
  );
}

export function recordFailureAnalysisFeedback(
  scope: RepoScope,
  analysisId: string,
  body: { kind: FailureFeedbackKind; note?: string },
): Promise<FailureAnalysisFeedback> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/failure-analyses/{analysis_id}/feedback',
      { params: { path: { ...repoPath(scope), analysis_id: analysisId } }, body },
    ),
  );
}

export function verifyFailureRecovery(
  scope: RepoScope,
  analysisId: string,
): Promise<FailureRecovery> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/failure-analyses/{analysis_id}/verify',
      { params: { path: { ...repoPath(scope), analysis_id: analysisId } } },
    ),
  );
}

/* The run, job and deployment detail reads the pages host the panel on. */

export function getWorkflowRun(scope: RepoScope, runId: string): Promise<Schemas['WorkflowRun']> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/runs/{run_id}', {
      params: { path: { ...repoPath(scope), run_id: runId } },
    }),
  );
}

export type RerunMode = Schemas['RerunMode'];

/**
 * Re-run a finished run: every job, or only the jobs it did not conclude
 * successfully (with their dependency closure; the rest carry their result).
 * The server answers with the new run, which names its source.
 */
export function rerunWorkflowRun(
  scope: RepoScope,
  runId: string,
  mode: RerunMode,
): Promise<Schemas['WorkflowRun']> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/runs/{run_id}/rerun', {
      params: { path: { ...repoPath(scope), run_id: runId } },
      body: { mode },
    }),
  );
}

/**
 * Cancel a run that has not finished. The server marks the jobs cancelled; a
 * runner learns of it on its next heartbeat and the execution plane destroys
 * the sandbox regardless, so the answer is the run as recorded now, not a
 * promise that every process has already stopped. A finished run is refused
 * with 412.
 */
export function cancelWorkflowRun(
  scope: RepoScope,
  runId: string,
  reason?: string,
): Promise<Schemas['WorkflowRun']> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/runs/{run_id}/cancel', {
      params: { path: { ...repoPath(scope), run_id: runId } },
      body: reason ? { reason } : {},
    }),
  );
}

export async function listWorkflowJobs(scope: RepoScope, runId: string): Promise<WorkflowJob[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/runs/{run_id}/jobs', {
      params: { path: { ...repoPath(scope), run_id: runId } },
    }),
  );
  return value.data;
}

export function getWorkflowJob(scope: RepoScope, jobId: string): Promise<WorkflowJobDetail> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/jobs/{job_id}', {
      params: { path: { ...repoPath(scope), job_id: jobId } },
    }),
  );
}

/**
 * A job's output after a cursor, as text. Authorized on every request, unlike
 * the pre-signed chunk URLs `getJobLogs` brokers; see docs/CICD_LIVE_LOGS.md.
 */
export function getJobLogText(scope: RepoScope, jobId: string, after: number): Promise<JobLogText> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/jobs/{job_id}/log-text', {
      params: { path: { ...repoPath(scope), job_id: jobId }, query: { after } },
    }),
  );
}

export function getDeployment(
  scope: RepoScope,
  deploymentId: string,
): Promise<Schemas['Deployment']> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/deployments/{deployment_id}', {
      params: { path: { ...repoPath(scope), deployment_id: deploymentId } },
    }),
  );
}
